import Link from 'next/link';

import { Anchor } from '@mantine/core';

import { all, Component, Manufacturer } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';

import { useCompanyProfile } from 'hooks/use-company-profile';
import { DateService } from 'services/DateService';

const useComponentMeta = (component: Component) => {
    const { company: manufacturer } = useCompanyProfile(component.manufacturer);

    const { identifier, name: uniqueName, slug } = ComponentHelpers.generateMetadata(component);

    const { typeName, subtitle } = useComponentMetaSubtitle(component.type, manufacturer);

    const price = component.msrp ? IntlHelpers.formatPriceShort(component.msrp) : '';
    const leadTime = component.leadTime ? DateService.formatNumberOfDays(component.leadTime) : '';

    return {
        uniqueName,
        identifier,
        typeName,
        subtitle,
        price,
        leadTime,
        slug,
        manufacturerName: manufacturer?.name,
        regionAvailability: component.regionAvailability?.map((region) => region)?.join(', ') || '',
    };
};

const useComponentMetaSubtitle = (type: Component['type'], manufacturer?: Pick<Manufacturer, 'name' | 'id'>) => {
    const typeName = all[type]?.name;

    const subtitle = manufacturer ? (
        <span>
            {typeName} •{' '}
            <Anchor
                component={Link}
                href={CompanyHelpers.urls.view(manufacturer.id)}
                underline="hover"
                inherit
                style={{ color: 'inherit' }}
            >
                {manufacturer.name}
            </Anchor>
        </span>
    ) : (
        typeName
    );

    return { subtitle, typeName };
};

export { useComponentMeta, useComponentMetaSubtitle };
